/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/no-magic-numbers */
import { ScreenSize } from '@libs/shared-services';
import {
  CEJ_PERCENTAGE,
  Coordinates,
  CROSS_COLOR,
  CROSS_OPACITY,
  CROSS_STROKE_WIDTH,
  drawPerio,
  FONT_FAMILY,
  FurcationRef,
  MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE,
  MeasurementCoordinates,
  MobilityRef,
  PERIO_CIRCLE_BLEEDING_COLOR,
  PERIO_CIRCLE_RADIUS,
  PERIO_CIRCLE_SUPPURATION_COLOR,
  PERIO_CONTAINER_HEIGHT,
  PERIO_CONTAINER_WIDTH,
  PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL,
  PocketDepthRef,
  SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY,
  Svg,
  SvgCircle,
  SvgGroup,
  SvgPath,
  SvgRect,
  SvgText,
  Tooth,
  TOOTH_PERIO_HEIGHT,
  TOOTH_WIDTH,
  ToothSide,
} from '@libs/ui-lib';
import { Selection } from 'd3-selection';
import { curveBumpX, line } from 'd3-shape';
import { PERIO_TOP_PADDING } from './teeth.constants';

export const straightLineGenerator = line<{ x: number; y: number }>()
  .x((d) => d.x)
  .y((d) => d.y);

export const perioLineGenerator = line<{ x: number; y: number }>()
  .x((d) => d.x)
  .y((d) => d.y)
  .curve(curveBumpX);

const isSvg = (svg: Svg | SvgGroup): svg is Svg => {
  return svg.hasOwnProperty('insert');
};

type PerioColor = typeof PERIO_CIRCLE_BLEEDING_COLOR | typeof PERIO_CIRCLE_SUPPURATION_COLOR;

export const drawPerioCircles = (
  svg: Svg | SvgGroup,
  coordinates: MeasurementCoordinates[],
  opacity = 1,
  circlesPosition: 'top' | 'bottom' = 'bottom',
  side?: ToothSide,
): Selection<SVGCircleElement, unknown, null, undefined>[] => {
  const circleElements: Selection<SVGCircleElement, unknown, null, undefined>[] = [];

  coordinates
    .filter(({ bleedingOnProbing, suppuration, pocketDepth }) => pocketDepth && (bleedingOnProbing || suppuration))
    .forEach(({ base, bleedingOnProbing, suppuration }) => {
      const fullCircle = (color: PerioColor, secondRow = false) => {
        const circleYPosition =
          circlesPosition === 'top'
            ? base!.y + TOOTH_PERIO_HEIGHT('xl') * (1 - CEJ_PERCENTAGE) - 12 - (secondRow ? 20 : 0)
            : base!.y + TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE + 12 + (secondRow ? 20 : 0);

        const circle = svg
          .append('circle')
          .attr('hide-in-compare', 'true')
          .attr('cx', base!.x + 2)
          .attr('cy', circleYPosition)
          .attr('r', PERIO_CIRCLE_RADIUS)
          .attr('fill', color)
          .attr('opacity', opacity)
          .attr('pointer-events', 'none')
          .attr('stroke', 'white')
          .attr('stroke-width', 2);
        if (side) {
          circle.attr('data-side', side);
        }
        circleElements.push(circle);
      };

      if (bleedingOnProbing) {
        fullCircle(PERIO_CIRCLE_BLEEDING_COLOR);
      }

      if (suppuration) {
        fullCircle(PERIO_CIRCLE_SUPPURATION_COLOR, true);
      }
    });

  return circleElements;
};

export const drawCross = (svg: Svg, screenSize: ScreenSize, containerHeight: number, containerWidth: number): SvgPath[] => {
  const crossPathRefs: SvgPath[] = [];
  crossPathRefs.push(
    svg
      .append('path')
      .attr(
        'd',
        straightLineGenerator([
          { x: containerWidth / 2, y: TOOTH_PERIO_HEIGHT('xl') - 10 },
          { x: containerWidth / 2, y: containerHeight - (TOOTH_PERIO_HEIGHT('xl') - 30) },
        ]),
      )
      .attr('stroke', CROSS_COLOR)
      .attr('stroke-width', CROSS_STROKE_WIDTH)
      .attr('opacity', CROSS_OPACITY)
      .attr('fill', 'none'),
    svg
      .append('path')
      .attr(
        'd',
        straightLineGenerator([
          { x: 0, y: containerHeight / 2 },
          { x: containerWidth - 30, y: containerHeight / 2 },
        ]),
      )
      .attr('stroke', CROSS_COLOR)
      .attr('stroke-width', CROSS_STROKE_WIDTH)
      .attr('opacity', CROSS_OPACITY)
      .attr('fill', 'none'),
  );

  return crossPathRefs;
};

export const drawBackgroundLines = (svg: Svg, teethCoordinates: Record<number, Coordinates>, cejPosition: 'lower' | 'upper'): void => {
  const cej_percentage = cejPosition === 'lower' ? CEJ_PERCENTAGE : 1 - CEJ_PERCENTAGE;
  //draw the CEJ line
  const firstToothCoordinate = teethCoordinates.hasOwnProperty(1) ? teethCoordinates[1] : teethCoordinates[32];
  const lastToothCoordinate = teethCoordinates.hasOwnProperty(16) ? teethCoordinates[16] : teethCoordinates[17];

  //draw 5 (or 12, depending on tooth side) lines below the CEJ lines, each line is 0.03649635036 percentage apart
  for (let i = 1; i < (cejPosition === 'lower' ? 6 : 13); i++) {
    drawBackgroundLine(
      svg,
      firstToothCoordinate,
      lastToothCoordinate,
      cej_percentage + i * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE,
      '#E6EDF3',
      2,
    );
  }
  //draw 10 (or 5, depending on tooth side) lines above the CEJ lines, each line is 0.03649635036 percentage apart
  for (let i = 1; i < (cejPosition === 'lower' ? 13 : 6); i++) {
    drawBackgroundLine(
      svg,
      firstToothCoordinate,
      lastToothCoordinate,
      cej_percentage - i * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE,
      '#E6EDF3',
      2,
    );
  }
};

export const drawBackgroundLine = (
  svg: Svg | SvgGroup,
  positionFrom: Coordinates,
  positionTo: Coordinates,
  yPositionPercentage: number,
  color: string,
  strokeWidth = 1,
): void => {
  const gumPath = perioLineGenerator([
    {
      x: positionFrom.x,
      y: positionFrom.y + TOOTH_PERIO_HEIGHT('xl') * yPositionPercentage,
    },
    {
      x: positionTo.x + TOOTH_WIDTH('xl'),
      y: positionTo.y + TOOTH_PERIO_HEIGHT('xl') * yPositionPercentage,
    },
  ]);
  if (!gumPath) return;

  drawPerio(svg, gumPath, { stroke: color, strokeWidth: strokeWidth, opacity: 1 });
};

export const drawSideLabels = (svg: Svg, height: number): (SvgRect | SvgText)[] => {
  /* eslint-disable @typescript-eslint/no-magic-numbers */
  //CEJ and numbers for top jaw buccal
  const topJawBuccalCejCoordinates: Coordinates = { x: 15, y: PERIO_TOP_PADDING + TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE };
  const topJawBuccalCej5Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 5,
  };
  const topJawBuccalCej10Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 10,
  };

  const topJawBuccalCej14Coordinates: Coordinates = {
    x: 16,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 14,
  };

  const topJawMobility: Coordinates = {
    x: 25,
    y: PERIO_TOP_PADDING + TOOTH_PERIO_HEIGHT('xl') + 11,
  };
  const topJawFurcation: Coordinates = {
    x: 28,
    y: PERIO_TOP_PADDING + TOOTH_PERIO_HEIGHT('xl') + 35,
  };

  //CEJ and numbers for top jaw lingual
  const topJawLingualCejCoordinates: Coordinates = {
    x: 15,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl'),
  };
  const topJawLingualCej5Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 5,
  };
  const topJawLingualCej10Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 10,
  };
  const topJawLingualCej14Coordinates: Coordinates = {
    x: 21,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 14,
  };

  const lowerJawLingualCejCoordinates: Coordinates = {
    x: 15,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      CEJ_PERCENTAGE * TOOTH_PERIO_HEIGHT('xl'),
  };

  const lowerJawLingualCej5Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      CEJ_PERCENTAGE * TOOTH_PERIO_HEIGHT('xl') -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 5,
  };
  const lowerJawLingualCej10Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      CEJ_PERCENTAGE * TOOTH_PERIO_HEIGHT('xl') -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 10,
  };

  const lowerJawLingualCej14Coordinates: Coordinates = {
    x: 16,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      CEJ_PERCENTAGE * TOOTH_PERIO_HEIGHT('xl') -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 13.3,
  };

  const lowerJawMobility: Coordinates = {
    x: 25,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      TOOTH_PERIO_HEIGHT('xl') +
      11,
  };
  const lowerJawFurcation: Coordinates = {
    x: 28,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      TOOTH_PERIO_HEIGHT('xl') +
      35,
  };

  const lowerJawFacialCejCoordinates: Coordinates = {
    x: 15,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl'),
  };

  const lowerJawFacialCej5Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 5,
  };

  const lowerJawFacialCej10Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 10,
  };
  const lowerJawFacialCej14Coordinates: Coordinates = {
    x: 21,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      TOOTH_PERIO_HEIGHT('xl') +
      SPACE_BETWEEN_MANIBULAR_AND_MAXILLARY +
      TOOTH_PERIO_HEIGHT('xl') +
      PERIO_SPACE_BETWEEN_FACIAL_AND_LINGUAL +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 14,
  };

  const textElements: (SvgText | SvgRect)[] = [];

  textElements.push(
    //this rectangle is the background for the CEJ grid texts on the left
    svg
      .append('rect')
      .attr('original-x', 0)
      .attr('quadrant-x', PERIO_CONTAINER_WIDTH('xl') / 2 - 20)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 30)
      .attr('height', height)
      .attr('fill', 'white'),
  );

  [
    {
      coordinates: topJawBuccalCejCoordinates,
      text: 'CEJ',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#035ADDA3',
    },
    {
      coordinates: topJawBuccalCej5Coordinates,
      text: '5',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
    },
    {
      coordinates: topJawBuccalCej10Coordinates,
      text: '10',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
    },
    {
      coordinates: topJawBuccalCej14Coordinates,
      text: 'Facial',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      lowerCase: true,
      textColor: '#3D4A56',
      hideInFullView: true,
      hideInLowerJaw: true,
    },

    {
      coordinates: topJawMobility,
      text: 'Mobility',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
      lowerCase: true,
    },
    {
      coordinates: topJawFurcation,
      text: 'Furcation',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
      lowerCase: true,
    },

    { coordinates: topJawLingualCejCoordinates, text: 'CEJ', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#035ADDA3' },
    { coordinates: topJawLingualCej5Coordinates, text: '5', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#3D4A56' },
    { coordinates: topJawLingualCej10Coordinates, text: '10', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#3D4A56' },
    {
      coordinates: topJawLingualCej14Coordinates,
      text: 'Lingual',
      rotate: false,
      lowerCase: true,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
      hideInFullView: true,
      hideInLowerJaw: true,
    },

    { coordinates: lowerJawLingualCejCoordinates, text: 'CEJ', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#035ADDA3' },
    { coordinates: lowerJawLingualCej5Coordinates, text: '5', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#3D4A56' },
    { coordinates: lowerJawLingualCej10Coordinates, text: '10', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#3D4A56' },
    {
      coordinates: lowerJawLingualCej14Coordinates,
      text: 'Lingual',
      lowerCase: true,
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
      hideInTopJaw: true,
      hideInFullView: true,
    },

    {
      coordinates: lowerJawMobility,
      text: 'Mobility',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
      lowerCase: true,
    },
    {
      coordinates: lowerJawFurcation,
      text: 'Furcation',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
      lowerCase: true,
    },

    { coordinates: lowerJawFacialCejCoordinates, text: 'CEJ', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#035ADDA3' },
    { coordinates: lowerJawFacialCej5Coordinates, text: '5', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#3D4A56' },
    { coordinates: lowerJawFacialCej10Coordinates, text: '10', rotate: false, fontSize: 12, fontWeight: 700, textColor: '#3D4A56' },
    {
      coordinates: lowerJawFacialCej14Coordinates,
      text: 'Facial',
      rotate: false,
      lowerCase: true,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#3D4A56',
      hideInFullView: true,
    },
  ].forEach(({ coordinates, text, rotate, fontSize, fontWeight, textColor, lowerCase, hideInFullView, hideInLowerJaw, hideInTopJaw }) => {
    textElements.push(
      svg
        .append('text')
        .attr('original-x', coordinates.x)
        .attr('quadrant-x', coordinates.x + PERIO_CONTAINER_WIDTH('xl') / 2 - 20)
        .attr('hide-in-full', hideInFullView ? 'true' : 'false')
        .attr('hide-in-top-jaw', hideInTopJaw ? 'true' : 'false')
        .attr('hide-in-lower-jaw', hideInLowerJaw ? 'true' : 'false')
        .attr('hide-in-compare', 'true')
        .attr('x', coordinates.x)
        .attr('y', coordinates.y)
        .attr('transform', rotate ? 'rotate(-90, 0, 0)' : '')
        .attr('transform-origin', `${coordinates.x}px ${coordinates.y}px`)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', textColor ?? '#9EA5AB')
        .attr('font-family', FONT_FAMILY)
        .attr('font-size', fontSize ?? 20)
        .attr('font-weight', fontWeight ?? 300)
        .attr('opacity', hideInFullView ? 0 : 1)
        .attr('style', 'user-select: none; -webkit-user-select: none;')
        .text(lowerCase ? text : text.toUpperCase()),
    );
  });
  return textElements;
};

export const drawInsideLabels = (svg: Svg): (SvgRect | SvgText)[] => {
  const textElements: (SvgText | SvgRect)[] = [];

  textElements.push(
    //these rectangles are the background of the "lingual" text in the middle of the perio chart
    svg
      .append('rect')
      .attr('hide-in-quadrant', 'true')
      .attr('original-x', PERIO_CONTAINER_WIDTH('xl') / 2 - 30)
      .attr('x', PERIO_CONTAINER_WIDTH('xl') / 2 - 30)
      .attr('y', PERIO_CONTAINER_HEIGHT('xl') / 2 - 10)
      .attr('width', 61)
      .attr('height', 20)
      .attr('fill', 'white')
      .attr('rx', 5),
    //these rectangles are the background of the "lingual" text in the middle of the perio chart
    svg
      .append('rect')
      .attr('hide-in-quadrant', 'true')
      .attr('original-x', 270)
      .attr('x', 270)
      .attr('y', PERIO_CONTAINER_HEIGHT('xl') / 2 - 10)
      .attr('width', 61)
      .attr('height', 20)
      .attr('fill', 'white')
      .attr('rx', 5),
    //these rectangles are the background of the "lingual" text in the middle of the perio chart
    svg
      .append('rect')
      .attr('hide-in-quadrant', 'true')
      .attr('original-x', PERIO_CONTAINER_WIDTH('xl') - 330)
      .attr('x', PERIO_CONTAINER_WIDTH('xl') - 330)
      .attr('y', PERIO_CONTAINER_HEIGHT('xl') / 2 - 10)
      .attr('width', 61)
      .attr('height', 20)
      .attr('fill', 'white')
      .attr('rx', 5),
  );

  const topLeftFacialTextCoordinate: Coordinates = { x: 300, y: 5 };
  const topMiddleFacialTextCoordinate: Coordinates = { x: PERIO_CONTAINER_WIDTH('xl') / 2, y: 5 };
  const topRightFacialTextCoordinate: Coordinates = { x: PERIO_CONTAINER_WIDTH('xl') - 300, y: 5 };

  const middleLeftLingualTextCoordinate: Coordinates = { x: 300, y: PERIO_CONTAINER_HEIGHT('xl') / 2 };
  const middleMiddleLingualTextCoordinate: Coordinates = { x: PERIO_CONTAINER_WIDTH('xl') / 2, y: PERIO_CONTAINER_HEIGHT('xl') / 2 };
  const middleRightLingualTextCoordinate: Coordinates = { x: PERIO_CONTAINER_WIDTH('xl') - 300, y: PERIO_CONTAINER_HEIGHT('xl') / 2 };

  const lowLeftFacialTextCoordinate: Coordinates = { x: 300, y: PERIO_CONTAINER_HEIGHT('xl') - 5 };
  const lowMiddleFacialTextCoordinate: Coordinates = { x: PERIO_CONTAINER_WIDTH('xl') / 2, y: PERIO_CONTAINER_HEIGHT('xl') - 5 };
  const lowRightFacialTextCoordinate: Coordinates = { x: PERIO_CONTAINER_WIDTH('xl') - 300, y: PERIO_CONTAINER_HEIGHT('xl') - 5 };

  [
    {
      coordinates: topLeftFacialTextCoordinate,
      text: 'Buccal',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: topMiddleFacialTextCoordinate,
      text: 'Facial',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: topRightFacialTextCoordinate,
      text: 'Buccal',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: middleLeftLingualTextCoordinate,
      text: 'Lingual',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: middleMiddleLingualTextCoordinate,
      text: 'Lingual',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: middleRightLingualTextCoordinate,
      text: 'Lingual',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: lowLeftFacialTextCoordinate,
      text: 'Buccal',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: lowMiddleFacialTextCoordinate,
      text: 'Facial',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
    {
      coordinates: lowRightFacialTextCoordinate,
      text: 'Buccal',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: '#0D1D2C',
      lowerCase: true,
    },
  ].forEach(({ coordinates, text, rotate, fontSize, fontWeight, textColor, lowerCase }) => {
    textElements.push(
      svg
        .append('text')
        .attr('original-x', coordinates.x)
        .attr('quadrant-x', coordinates.x + PERIO_CONTAINER_WIDTH('xl') / 2 - 20)
        .attr('hide-in-quadrant', 'true')
        .attr('x', coordinates.x)
        .attr('y', coordinates.y)
        .attr('transform', rotate ? 'rotate(-90, 0, 0)' : '')
        .attr('transform-origin', `${coordinates.x}px ${coordinates.y}px`)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', textColor ?? '#9EA5AB')
        .attr('font-family', FONT_FAMILY)
        .attr('font-size', fontSize ?? 20)
        .attr('font-weight', fontWeight ?? 300)
        .attr('opacity', 1)
        .attr('style', 'user-select: none; -webkit-user-select: none;')
        .attr('data-visibility', '{"fullView": true, "compareView": true}')
        .text(lowerCase ? text : text.toUpperCase()),
    );
  });
  return textElements;
};

export const drawJawPlacementText = (
  svg: Svg,
  height: number,
  width: number,
  containerPaddingX: number,
  containerPaddingY: number,
): SvgText[] => {
  const textRefs: SvgText[] = [];
  const quarterY = (height - containerPaddingY * 2) / 4;
  const maxillaryCoordinates: Coordinates = { x: containerPaddingX / 2 - 20, y: containerPaddingY + quarterY };
  const mandibularCoordinates: Coordinates = { x: containerPaddingX / 2 - 20, y: containerPaddingY + quarterY * 3 };
  [
    { coordinates: maxillaryCoordinates, text: 'Maxillary', rotate: true },
    { coordinates: mandibularCoordinates, text: 'Mandibular', rotate: true },
  ].forEach(({ coordinates, text, rotate }) => {
    textRefs.push(
      svg
        .append('text')
        .attr('hide-in-quadrant', 'true')
        .attr('x', coordinates.x)
        .attr('y', coordinates.y)
        .attr('transform', rotate ? 'rotate(-90, 0, 0)' : '')
        .attr('transform-origin', `${coordinates.x}px ${coordinates.y}px`)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#9EA5AB')
        .attr('font-family', FONT_FAMILY)
        .attr('font-size', 20)
        .attr('font-weight', 300)
        .attr('style', 'user-select: none; -webkit-user-select: none;')
        .text(text.toUpperCase()),
    );
  });
  return textRefs;
};

export const drawMobility = (
  svg: Svg | SvgGroup,
  teeth: Tooth[],
  teethCoordinates: Record<number, Coordinates>,
  color: string,
  mobilityPosition: 'below' | 'above',
): MobilityRef[] => {
  const mobilityRefs: MobilityRef[] = [];

  teeth.forEach((tooth) => {
    const position = tooth.position;
    const coordinates = teethCoordinates[position];
    const mobility = tooth.mobility;

    if (!mobility) {
      return;
    }

    const mobilityX = coordinates.x + TOOTH_WIDTH('xl') / 2 - 13;
    const mobilityY = mobilityPosition === 'below' ? coordinates.y + TOOTH_PERIO_HEIGHT('xl') : coordinates.y - 45;

    mobilityRefs.push(
      svg
        .append('rect')
        .attr('hide-in-compare', 'true')
        .attr('x', mobilityX)
        .attr('y', mobilityY)
        .attr('width', 27)
        .attr('height', 17)
        .attr('rx', 8)
        .attr('ry', 8)
        .attr('fill', color)
        .attr('pointer-events', 'none'),
      svg
        .append('text')
        .attr('hide-in-compare', 'true')
        .attr('hide-in-preview', 'true')
        .attr('x', mobilityX + 13)
        .attr('y', mobilityY + 9)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', 'white')
        .attr('font-family', FONT_FAMILY)
        .attr('font-size', 12)
        .attr('font-weight', 'bold')
        .attr('style', 'user-select: none; -webkit-user-select: none;')
        .text(mobility),
    );
  });

  return mobilityRefs;
};

export const drawFurcation = (
  svg: Svg | SvgGroup,
  teeth: Tooth[],
  teethCoordinates: Record<number, Coordinates>,
  color: string,
  mobilityPosition: 'below' | 'above',
): FurcationRef[] => {
  const furcationRefs: FurcationRef[] = [];

  teeth.forEach((tooth) => {
    const position = tooth.position;
    const coordinates = teethCoordinates[position];
    const furcationValue = Math.max(
      tooth.perio.buccal.furcation.central,
      tooth.perio.buccal.furcation.distal,
      tooth.perio.buccal.furcation.mesial,

      tooth.perio.lingual.furcation.central,
      tooth.perio.lingual.furcation.distal,
      tooth.perio.lingual.furcation.mesial,
    );

    if (!furcationValue) {
      return;
    }

    const furcationX = coordinates.x + TOOTH_WIDTH('xl') / 2 - 2;
    const furcationY = mobilityPosition === 'below' ? coordinates.y + TOOTH_PERIO_HEIGHT('xl') + 23 : coordinates.y - 20;

    const topRoundedCorner = 'a2 2 0 0 1 3.5 0'; // 3.5 is the radius of the rounded corner
    const bottomRightCorner = 'a2 2 0 0 1 -1.7320508075689 3';
    const bottomLeftCorner = 'a2 2 0 0 1 -1.7320508075689 -3';
    const triangleRightLeg = `l11 15`;
    const triangleBottomLeg = `l-22 0`;

    furcationRefs.push(
      svg
        .append('path')
        .attr('hide-in-compare', 'true')
        .attr(
          'd',
          `M
          ${furcationX}
          ${furcationY}
          ${topRoundedCorner}
          ${triangleRightLeg}
          ${bottomRightCorner}
          ${triangleBottomLeg}
          ${bottomLeftCorner}`,
        )
        .attr('fill', color)
        .attr('pointer-events', 'none'),
      svg
        .append('text')
        .attr('hide-in-compare', 'true')
        .attr('hide-in-preview', 'true')
        .attr('x', furcationX + 2)
        .attr('y', furcationY + 11)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', 'white')
        .attr('font-family', FONT_FAMILY)
        .attr('font-size', 12)
        .attr('font-weight', 'bold')
        .attr('style', 'user-select: none; -webkit-user-select: none;')
        .text(furcationValue),
    );
  });

  return furcationRefs;
};

export const drawPocketDepth = (
  svg: Svg | SvgGroup,
  teeth: Tooth[],
  coordinates: MeasurementCoordinates[],
  opacity = 1,
  cejPosition: 'upper' | 'lower',
  side: ToothSide,
  viewMode: 'normal' | 'perio' = 'normal',
  fullDisplayMinValue = 2,
): PocketDepthRef[] => {
  const unitHeight = TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE;
  const pocketDepthRefs: PocketDepthRef[] = [];

  coordinates.forEach(({ pocketDepth, gingivalMargin }) => {
    const triangleTopYCord = pocketDepth!.y;
    const pocketDepthGroup = svg
      .append('g')
      .attr('data-side', side || '')
      // If caller requested quadrant mode, always show these elements fully; otherwise use provided opacity
      .attr('opacity', viewMode === 'perio' ? 1 : opacity)
      .attr('hide-in-compare', 'true');

    if (pocketDepth?.value && pocketDepth.value > 1) {
      //draw triangle, which has a height of 2 units.

      const topRoundedCorner = 'a2 2 0 0 1 3.5 0'; // 3.5 is the radius of the rounded corner
      //we don't need the bottom corners if the pocket depth is greater than 2
      const bottomRightCorner = pocketDepth.value > 2 ? '' : 'a2 2 0 0 1 -1.7320508075689 3';
      const bottomLeftCorner = pocketDepth.value > 2 ? '' : 'a2 2 0 0 1 -1.7320508075689 -3';

      const triangleRightLeg = `l8 ${bottomRightCorner ? unitHeight * 2 - 2.5 : unitHeight * 2 + 0.5}`;

      //the bottom leg should be longer if we don't have a bottom corners
      const triangleBottomLeg = `l${bottomRightCorner ? -16 : -19.73} 0`;
      const trianglePath = pocketDepthGroup
        .append('path')
        .attr('data-side', side || '')
        .attr('hide-in-compare', 'true')
        .attr(
          'd',
          `M
          ${pocketDepth!.x}
          ${triangleTopYCord}
          ${topRoundedCorner}
          ${triangleRightLeg}
          ${bottomRightCorner}
          ${triangleBottomLeg}
          ${bottomLeftCorner}`,
        )
        .attr('fill', '#9747ff')
        .attr('pointer-events', 'none')
        //if cej position is upper, we need to mirror everything to the gingival margin line so that the purple triangles will point downwards
        .attr('transform-origin', `${gingivalMargin!.x}px ${gingivalMargin.y}px`)
        .attr('transform', cejPosition === 'upper' ? 'scale(1,-1)' : '')
        // ensure initial opacity matches the requested viewMode; quadrant forces visible shapes
        .attr('opacity', viewMode === 'perio' ? 1 : opacity);
      pocketDepthRefs.push(trianglePath);

      //if the pocket depth is greater than 2, we need to draw a rectangle below the triangle (which is 2 unit high.)
      if (pocketDepth?.value && pocketDepth.value > 2) {
        const rectHeight = unitHeight * (pocketDepth.value - 2);
        //the maximum rectangle height is 10 units, because 12mm is the max height pocket depth.
        const maxRectHeight = unitHeight * (10 - (gingivalMargin?.value ?? 0));
        const squareRect = pocketDepthGroup
          .append('rect')
          .attr('data-side', side || '')
          .attr('hide-in-compare', 'true')
          .attr('x', pocketDepth!.x - 8.5)
          .attr('y', triangleTopYCord + unitHeight * 2 - 0.2)
          .attr('width', 20.5)
          .attr('height', Math.min(rectHeight, maxRectHeight))
          .attr('fill', '#9747ff')
          .attr('rx', 2)
          //if cej position is upper, we need to mirror everything to the gingival margin line so that the purple triangles will point downwards
          .attr('transform-origin', `${gingivalMargin!.x}px ${gingivalMargin.y}px`)
          .attr('transform', cejPosition === 'upper' ? 'scale(1,-1)' : '')
          .attr('opacity', viewMode === 'perio' ? 1 : opacity);
        pocketDepthRefs.push(squareRect);
      }
    }

    if (pocketDepth?.value === 1) {
      //draw a rectangle, which has a height of 1 units.
      const rect = pocketDepthGroup
        .append('rect')
        .attr('data-side', side || '')
        .attr('hide-in-compare', 'true')
        .attr('x', pocketDepth!.x - 8.5)
        .attr('y', triangleTopYCord)
        .attr('width', 20)
        .attr('height', unitHeight)
        .attr('fill', '#9747ff')
        .attr('rx', 2)
        //if cej position is upper, we need to mirror everything to the gingival margin line so that the purple triangles will point downwards
        .attr('transform-origin', `${gingivalMargin!.x}px ${gingivalMargin.y}px`)
        .attr('transform', cejPosition === 'upper' ? 'scale(1,-1)' : '')
        .attr('opacity', viewMode === 'perio' ? 1 : opacity);
      pocketDepthRefs.push(rect);
    }

    //draw a text, which shows the pocket depth value.
    if (pocketDepth?.value) {
      const textYCoordinateForQuadrantView =
        gingivalMargin.y -
        6 +
        (cejPosition === 'upper' ? 13 : 0) -
        (cejPosition === 'upper' && pocketDepth.value === 1 ? 3 : 0) +
        (cejPosition === 'lower' && pocketDepth.value === 1 ? 3 : 0);

      const textYCoordinateNormal = gingivalMargin.y - 8 + (cejPosition === 'upper' ? 19 : 0);

      // Determine the initial font-size and y position depending on the requested viewMode
      const initialFontSize = determineInitialPDFontSize(pocketDepth.value);
      const initialOpacity = pocketDepth.value < fullDisplayMinValue ? 0 : 1;

      const text = pocketDepthGroup
        .append('text')
        .attr('hide-in-full', pocketDepth.value < fullDisplayMinValue)
        .attr('hide-in-compare', pocketDepth.value < fullDisplayMinValue)
        .attr('hide-in-preview', 'true')
        .attr('opacity', initialOpacity)
        .attr('x', pocketDepth!.x + 2)
        //if cej position is upper, we need to shift down the text a little bit
        .attr('y-normal', textYCoordinateNormal)
        .attr('y-quadrant', textYCoordinateForQuadrantView)
        .attr('y', pocketDepth.value > 2 ? textYCoordinateNormal : textYCoordinateForQuadrantView)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', 'white')
        .attr('font-family', FONT_FAMILY)
        .attr('big-font-size', 13)
        .attr('small-font-size', 9)
        .attr('extra-small-font-size', 7)
        .attr('has-one-value', pocketDepth.value === 1)
        .attr('font-size', initialFontSize)
        .attr('font-weight', 'bold')
        .attr('style', 'user-select: none; -webkit-user-select: none;')
        .text(pocketDepth.value);
      pocketDepthRefs.push(text);
    }
  });
  return pocketDepthRefs;
};

export const drawGMLineByCoordinates = (
  svg: Svg | SvgGroup,
  gumPerioCoordinates: Coordinates[],
  side: ToothSide,
  opacity: number = 1,
): SvgPath | undefined => {
  const gumPath = perioLineGenerator(gumPerioCoordinates);
  if (!gumPath) return;

  return drawLine(svg, gumPath, { stroke: '#E8489F', strokeWidth: 3, opacity }, undefined, undefined, side);
};

export const drawLine = (
  svg: Svg | SvgGroup,
  path: string,
  options?: Partial<{
    fill: string;
    stroke: string;
    strokeWidth: number;
    strokeDasharray: string;
    opacity: number;
  }>,
  id?: string,
  insertBeforeId?: string,
  side?: ToothSide,
): SvgPath => {
  const selection = insertBeforeId && isSvg(svg) ? svg.insert('path', `#${insertBeforeId}`) : svg.append('path');

  selection
    .attr('id', id ?? '')
    .attr('d', path)
    .attr('hide-in-compare', 'true')
    .attr('stroke', options?.stroke ?? 'none')
    .attr('stroke-width', options?.strokeWidth ?? 0)
    .attr('stroke-dasharray', options?.strokeDasharray ?? 'none')
    .attr('stroke-linecap', 'round')
    .attr('fill', options?.fill ?? 'none')
    .attr('opacity', options?.opacity ?? 0)
    .attr('pointer-events', 'none');

  if (side) {
    selection.attr('data-side', side);
  }

  return selection;
};

export const drawPerioTreatments = (
  svg: Svg | SvgGroup,
  upperJawTeeth: Tooth[],
  lowerJawTeeth: Tooth[],
  hasPerioMeasurements: boolean,
  upperJawBuccalPeriosCoordinates: MeasurementCoordinates[],
  upperJawLingualPeriosCoordinates: MeasurementCoordinates[],
  lowerJawLingualPeriosCoordinates: MeasurementCoordinates[],
  lowerJawBuccalPeriosCoordinates: MeasurementCoordinates[],
  upperJawBuccalTeethCoordinates: Record<number, Coordinates>,
  lowerJawBuccalTeethCoordinates: Record<number, Coordinates>,
  toothSide?: ToothSide,
): [PocketDepthRef[], MobilityRef[], FurcationRef[], SvgCircle[], SvgPath[]] => {
  // Remove existing treatmentsLayer if it exists
  svg.select('[data-id="perio-treatments-layer"]').remove();

  const pocketDepthRefs: PocketDepthRef[] = [];
  const mobilityRefs: MobilityRef[] = [];
  const furcationRefs: FurcationRef[] = [];
  const gmLinePathRefs: SvgPath[] = [];
  const bleedingAndSuppurationRefs: SvgCircle[] = [];

  // Create a new treatmentsLayer
  const treatmentsLayer = svg.append('g').attr('data-id', 'perio-treatments-layer');
  const buccalOpacity = toothSide === 'lingual' ? 0.5 : 1;
  const lingualOpacity = toothSide === 'buccal' ? 0.5 : 1;

  if (hasPerioMeasurements) {
    const gmLinePaths = [
      drawGMLineByCoordinates(
        treatmentsLayer,
        upperJawBuccalPeriosCoordinates.map((t) => ({ x: t.gingivalMargin.x, y: t.gingivalMargin.y })),
        'buccal',
        buccalOpacity,
      ),
      drawGMLineByCoordinates(
        treatmentsLayer,
        upperJawLingualPeriosCoordinates.map((t) => ({ x: t.gingivalMargin.x, y: t.gingivalMargin.y })),
        'lingual',
        lingualOpacity,
      ),
      drawGMLineByCoordinates(
        treatmentsLayer,
        lowerJawBuccalPeriosCoordinates.map((t) => ({ x: t.gingivalMargin.x, y: t.gingivalMargin.y })),
        'buccal',
        buccalOpacity,
      ),
      drawGMLineByCoordinates(
        treatmentsLayer,
        lowerJawLingualPeriosCoordinates.map((t) => ({ x: t.gingivalMargin.x, y: t.gingivalMargin.y })),
        'lingual',
        lingualOpacity,
      ),
    ];

    gmLinePaths.forEach((path) => {
      if (path) gmLinePathRefs.push(path);
    });
  }

  // Draw pocket depths
  const upperJawBuccalPocketDepths = drawPocketDepth(
    treatmentsLayer,
    upperJawTeeth,
    upperJawBuccalPeriosCoordinates,
    buccalOpacity,
    'lower',
    'buccal',
  );
  const upperJawLingualPocketDepths = drawPocketDepth(
    treatmentsLayer,
    upperJawTeeth,
    upperJawLingualPeriosCoordinates,
    lingualOpacity,
    'lower',
    'lingual',
  );
  const lowerJawBuccalPocketDepths = drawPocketDepth(
    treatmentsLayer,
    lowerJawTeeth,
    lowerJawBuccalPeriosCoordinates,
    buccalOpacity,
    'upper',
    'buccal',
  );
  const lowerJawLingualPocketDepths = drawPocketDepth(
    treatmentsLayer,
    lowerJawTeeth,
    lowerJawLingualPeriosCoordinates,
    lingualOpacity,
    'upper',
    'lingual',
  );

  pocketDepthRefs.push(
    ...upperJawBuccalPocketDepths,
    ...upperJawLingualPocketDepths,
    ...lowerJawBuccalPocketDepths,
    ...lowerJawLingualPocketDepths,
  );

  // Draw perio circles
  bleedingAndSuppurationRefs.push(
    ...drawPerioCircles(treatmentsLayer, upperJawBuccalPeriosCoordinates, buccalOpacity, 'bottom', 'buccal'),
    ...drawPerioCircles(treatmentsLayer, upperJawLingualPeriosCoordinates, lingualOpacity, 'bottom', 'lingual'),
    ...drawPerioCircles(treatmentsLayer, lowerJawBuccalPeriosCoordinates, buccalOpacity, 'top', 'buccal'),
    ...drawPerioCircles(treatmentsLayer, lowerJawLingualPeriosCoordinates, lingualOpacity, 'top', 'lingual'),
  );

  // Draw mobility
  mobilityRefs.push(
    ...drawMobility(treatmentsLayer, upperJawTeeth, upperJawBuccalTeethCoordinates, '#F59943', 'below'),
    ...drawMobility(treatmentsLayer, lowerJawTeeth, lowerJawBuccalTeethCoordinates, '#F59943', 'above'),
  );
  // Draw furcation
  furcationRefs.push(
    ...drawFurcation(treatmentsLayer, upperJawTeeth, upperJawBuccalTeethCoordinates, '#10589B', 'below'),
    ...drawFurcation(treatmentsLayer, lowerJawTeeth, lowerJawBuccalTeethCoordinates, '#10589B', 'above'),
  );

  return [pocketDepthRefs, mobilityRefs, furcationRefs, bleedingAndSuppurationRefs, gmLinePathRefs];
};

const determineInitialPDFontSize = (pocketDepth: number): number => {
  if (pocketDepth === 1) {
    return 7;
  }

  if (pocketDepth == 2) {
    return 9;
  }

  return 13;
};
